import { environment } from '@root/environments/environment';
import { Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { InfoBannerComponent } from '@custom-components/feedback/info-banner/info-banner.component';
import { Store } from '@ngrx/store';
import { Subject, SubscriptionLike } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { DeviceModel, FirmwareModel } from '@core/models';
import { TranslateModule } from '@ngx-translate/core';
import { FirmwareActions, selectFirmwareUpdateSuccess } from '@root/app/store/firmware.index';
import { FirmwareSSEService } from '@core/services/firmware.sse.service';
import { FIRMWARE_TRANSPORT, FirmwareTransport } from '@core/utils/firmware-catalogue.util';

export interface FirmwareUpdateDialogData {
  device: DeviceModel;
  transport: FirmwareTransport;
  availableFirmwares: FirmwareModel[];
  currentVersion: string;
  /** The installed version is not a catalogue row, so the offered versions are not an upgrade
   * path and have to be labelled as such. */
  installedNotInCatalogue?: boolean;
}

type UpdateStage =
  | 'form'
  | 'starting'
  | 'in_progress'
  | 'success'
  | 'failed'
  | 'error'
  | 'submitted';

@Component({
  selector: 'xemex-firmware-update-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    SpinnerComponent,
    InfoBannerComponent,
    TranslateModule,
  ],
  templateUrl: './firmware-update-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './firmware-update-dialog.component.scss',
})
export class FirmwareUpdateDialogComponent implements OnDestroy {
  readonly dialogRef = inject(MatDialogRef<FirmwareUpdateDialogComponent>);
  readonly data = inject<FirmwareUpdateDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly destroy$ = new Subject<void>();
  private eventSubscription?: SubscriptionLike;

  form: FormGroup;
  updateStage: UpdateStage = 'form';
  sseError: string | undefined = undefined;

  constructor(private readonly eventSourceService: FirmwareSSEService) {
    this.form = this.fb.group({
      firmwareId: [null, Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.closeSseConnection();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private closeSseConnection(): void {
    this.eventSubscription?.unsubscribe();
  }

  get device(): DeviceModel {
    return this.data.device;
  }

  get availableFirmwares(): FirmwareModel[] {
    return this.data.availableFirmwares;
  }

  get currentVersion(): string {
    return this.data.currentVersion;
  }

  get installedNotInCatalogue(): boolean {
    return !!this.data.installedNotInCatalogue;
  }

  /** A DLMS update takes several minutes and has no streaming channel, so the dialog confirms on
   * the 202 and sends the user to the Activity tab instead of tracking progress. */
  get isDlms(): boolean {
    return this.data.transport === FIRMWARE_TRANSPORT.Dlms;
  }

  get showForm(): boolean {
    return this.updateStage === 'form';
  }

  get showStarting(): boolean {
    return this.updateStage === 'starting';
  }

  get showInProgress(): boolean {
    return this.updateStage === 'in_progress';
  }

  get showSuccess(): boolean {
    return this.updateStage === 'success';
  }

  get showSubmitted(): boolean {
    return this.updateStage === 'submitted';
  }

  get showError(): boolean {
    return this.updateStage === 'failed' || this.updateStage === 'error';
  }

  get statusMessage(): string {
    if (this.updateStage === 'starting') return 'firmwareManagement.updateDialog.stages.starting';
    if (this.updateStage === 'in_progress')
      return 'firmwareManagement.updateDialog.stages.inProgress';
    if (this.updateStage === 'success') return 'firmwareManagement.updateDialog.sse.success';
    if (this.updateStage === 'failed') return 'firmwareManagement.updateDialog.sse.failed';
    if (this.updateStage === 'error')
      return this.sseError || 'firmwareManagement.updateDialog.sse.error';
    return '';
  }

  private get selectedFirmware(): FirmwareModel | undefined {
    const firmwareId = this.form.value.firmwareId as number | null;
    return this.availableFirmwares.find(fw => fw.firmwareId === firmwareId);
  }

  onCancel(): void {
    this.closeSseConnection();
    this.dialogRef.close();
  }

  onUpdate(): void {
    const firmware = this.selectedFirmware;
    if (!this.form.valid || !firmware) return;

    this.updateStage = 'starting';

    if (this.isDlms) {
      this.startDlmsUpdate(firmware);
      return;
    }

    this.store.dispatch(
      FirmwareActions.updateDeviceFirmware({
        request: {
          device_id: String(this.device.deviceId),
          device_type: this.data.transport,
          version: firmware.version,
        },
      }),
    );
    this.connectToSse();
  }

  /** No stream to follow, so the dialog confirms on the 202 and stops — but it still waits for
   * that 202: reporting "started" straight after the dispatch would hide a rejected request. */
  private startDlmsUpdate(firmware: FirmwareModel): void {
    this.store.dispatch(
      FirmwareActions.startDlmsFirmwareUpdate({
        deviceId: String(this.device.deviceId),
        firmwareId: firmware.firmwareId,
      }),
    );

    this.store
      .select(selectFirmwareUpdateSuccess)
      .pipe(
        filter(success => success !== undefined),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(success => {
        if (success) {
          this.updateStage = 'submitted';
          return;
        }
        this.sseError = 'firmwareManagement.updateDialog.updateError';
        this.updateStage = 'error';
      });
  }

  private async connectToSse(): Promise<void> {
    this.updateStage = 'in_progress';
    const deviceId = String(this.device.deviceId);
    const url = `${environment.api.url}/firmware/devices/${deviceId}/updates/stream`;

    this.eventSubscription = this.eventSourceService
      .connectEventSource(
        url,
        {
          headers: {
            'api-version': 'v1',
          },
        },
        ['update'],
      )
      .subscribe({
        next: data => {
          try {
            const parsed = JSON.parse(data.data);

            if (parsed.update) {
              if (parsed.update === 'success') {
                this.updateStage = 'success';
                this.closeSseConnection();
              } else if (parsed.update === 'failed') {
                this.updateStage = 'failed';
                this.closeSseConnection();
              }
            }

            if (parsed.error) {
              this.sseError = parsed.error;
              this.updateStage = 'error';
              this.closeSseConnection();
            }
          } catch (e) {
            console.error('Failed to parse SSE message:', e);
          }
        },
        error: () => {
          this.updateStage = 'error';
          this.closeSseConnection();
        },
      });
  }

  onClose(): void {
    this.closeSseConnection();
    this.dialogRef.close();
  }
}
